<template>
    <div class="base-card__content ondemand-content base-card__zoom" :class="classesOndemandContent">
        <div class="ondemand-content__wrapper base-card__shadow">
           <img 
                v-if="imageUrl" 
                loading="lazy" 
                alt=""
                class="ondemand-content__image"
                :class="{'ondemand-content__image_blured': isParentallyRestricted}"
                :src="imageUrl"
            >
            <common-icon v-else class="base-card__shadow ondemand-content__image" :icon-name="'IconNoPicture'" />

            <div v-show="isRemovedFromFavorite" class="ondemand-content__favorite-label">{{ captionRemoved }}</div>
        </div>
        <common-icon v-if="isParentallyRestricted" class="ondemand-content__image-lock" :icon-name="'IconLock'" />
        <div class="base-card__shift ondemand-content__name">
            {{ title }}
        </div>
    </div>
</template>

<script setup lang="ts">
    import { type PropType, computed, type ComputedRef } from "vue";
    import { type OnDemandCardData } from "../../models";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useFavorite } from "@/composables/useFavorite";
    import { Strings } from "@/locales/types/enums";
    import { useI18n } from "vue-i18n";
    import { useProgramInfo } from "@/composables/useProgramInfo/useProgramInfo";
    import { type OnDemandItem } from "@ca.vmedia.tv/domains";
    import { AppConstants } from "@/common";
    import { ImgUrlUtils } from '@/utils';
    import { ShowTypes } from "@/components/Player/types/enums/ShowTypes";

    const { hasDeletedIdsFavoriteOnDemands } = useFavorite();

    const props = defineProps({
        cardData: {
            type: Object as PropType<OnDemandCardData>,
            required: true
        },
        /**
         * The flag to indicate if the program info should be enabled
         */
        enableProgramInfo: {
            type: Boolean,
            required: false,
            default: false
        },
    });

    const { $domains } = useNuxtApp();
    const { HeaderImageWidth }: typeof AppConstants = AppConstants;
    const emit = defineEmits(["program-info"]);
    const { t } = useI18n();
    const { needsUnlockPin } = useParentControl();
    const captionRemoved: string = t(Strings.CARD_REMOVED).toString();

    const imageUrl = computed((): string => {
        const image = props.cardData.getImage();

        if (!image) {
            return '';
        } else {
            return `${image}&width=260`;
        }
    });
    const title = computed((): string =>  props.cardData.getName());
    const isParentallyRestricted = computed((): boolean => needsUnlockPin(props.cardData.getPlayItem()?.advisory));

    const isRemovedFromFavorite: ComputedRef<boolean> = computed(() => hasDeletedIdsFavoriteOnDemands(`${props.cardData.getId()}:${props.cardData.getContentType()}`));
    const classesOndemandContent: ComputedRef<{[key: string]: boolean}> = computed(() => {
        return {
            'ondemand-content_isRemovedFromFavorite': isRemovedFromFavorite.value
        };
    });

    if (props.enableProgramInfo) {
        const createProgramInfo = async (item: OnDemandItem): Promise<void> => {
            if(item === null) {
                return;
            }
             
            let imgUrl = item.showType === ShowTypes.Series 
                ? await $domains.imagesRepository.getOnDemandEpisodeImage(item.images) 
                :  await $domains.imagesRepository.getOnDemandBackgroundImage(item.images);

            if(imgUrl === null) {
                imgUrl = await $domains.imagesRepository.getOnDemandImage(item.images) 
            }
                
            const srcImg = ImgUrlUtils.src(imgUrl, HeaderImageWidth);
            const programInfo = useProgramInfo(item, null, srcImg);
            
            emit("program-info", programInfo);
        };

        createProgramInfo(props.cardData.getPlayItem());
    }
</script>

<style lang="scss">
    @import url("styles.scss");
</style>